/**
 * What picking a result that is a spot on the ground does in the Explore
 * search — a typed coordinate, a place from our place search, or one of our
 * POIs: on any page with a map, move that map there (and open a POI's sheet
 * where the page can show one); with no map, go to a page that shows it.
 */
export type MapResultAction =
  | { kind: 'fly'; center: [number, number]; zoom: number }
  | { kind: 'fit'; bounds: [number, number, number, number] }
  | { kind: 'sheet'; center: [number, number]; zoom: number; poi: PoiSheet }
  | { kind: 'navigate'; url: string };

/** What window.showPoi needs to open a POI's sheet, as a click on its pin passes it. */
export interface PoiSheet {
  categoryType: string;
  id: string;
  type: string | null;
  name: string;
}

export interface SearchPoi {
  /** The search document id, `POI_<id>` — the same id the map's POI tiles carry. */
  id: string;
  name: string;
  url: string;
  /** `accommodation` or `amenity`: the two POI tables have overlapping ids. */
  categoryType: string | null;
  subType: string | null;
  /** The search document's `location`, [lat, lng]; [0, 0] when the POI has no coordinate. */
  latLng: number[] | null;
}

export interface SearchLocation {
  lat: number;
  lon: number;
  /** [west, south, east, north], or null for a point. */
  bbox: [number, number, number, number] | null;
}

export const COORDINATE_ZOOM = 15;
export const POI_ZOOM = 15;
export const LOCATION_ZOOM = 12;

const POI_CATEGORY_TYPES = ['accommodation', 'amenity'];

export function coordinateMapAction(coordinate: { lat: number; lng: number }, page: { hasMap: boolean }): MapResultAction {
  if (page.hasMap) return { kind: 'fly', center: [coordinate.lng, coordinate.lat], zoom: COORDINATE_ZOOM };
  return { kind: 'navigate', url: `/map?lat=${coordinate.lat}&lng=${coordinate.lng}` };
}

// A town or a county frames its own extent; a point (an address, a peak) gets a close-in zoom.
export function locationMapAction(location: SearchLocation, page: { hasMap: boolean }): MapResultAction {
  if (!page.hasMap) return { kind: 'navigate', url: `/explore?lat=${location.lat}&lng=${location.lon}&zoom=${LOCATION_ZOOM}` };
  const bbox = location.bbox;
  if (bbox && bbox[0] < bbox[2] && bbox[1] < bbox[3]) return { kind: 'fit', bounds: bbox };
  return { kind: 'fly', center: [location.lon, location.lat], zoom: LOCATION_ZOOM };
}

export function poiMapAction(poi: SearchPoi, page: { hasMap: boolean; canShowPlaces: boolean }): MapResultAction {
  const center = poiCenter(poi);
  if (!center) return { kind: 'navigate', url: poi.url };
  // Search can hold a POI with no page of its own; the map at it is the next best thing.
  if (!page.hasMap) return { kind: 'navigate', url: poi.url || `/map?lat=${center[1]}&lng=${center[0]}` };
  if (page.canShowPlaces && poi.categoryType && POI_CATEGORY_TYPES.includes(poi.categoryType)) {
    return {
      kind: 'sheet',
      center,
      zoom: POI_ZOOM,
      poi: { categoryType: poi.categoryType, id: poi.id, type: poi.subType, name: poi.name },
    };
  }
  return { kind: 'fly', center, zoom: POI_ZOOM };
}

function poiCenter(poi: SearchPoi): [number, number] | null {
  const [lat, lng] = poi.latLng ?? [];
  if (typeof lat !== 'number' || typeof lng !== 'number') return null;
  if (lat === 0 && lng === 0) return null;
  return [lng, lat];
}
